// Task 24584 — "who else is off at the same time", inside the leave request
// modal. A small calendar block covering the month either side of the request,
// in the same month-rows/day-columns shape as the Staff Leave calendar, plus
// the list that actually matters when you are about to approve: who is already
// away on the days being asked for.
//
// This calls searchStaffLeaveApi directly rather than dispatching the redux
// action. The table sitting behind this modal reads the same
// `searchStaffLeaveSuccess` slice, so dispatching here would overwrite the list
// the user came from with a company-wide one.

import React, { useEffect, useMemo, useState } from "react";
import LoadingSpinner from "../../Common/Spinner";
import { searchStaffLeaveApi } from "../../../helpers/fakebackend_helper";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// 31 columns always, so the 14th of one month sits above the 14th of the next.
const DAYS_IN_ROW = 31;

// A declined request is not somebody being off.
const DECLINED_STATUS = "3";
const APPROVED_STATUS = "2";

// Dates come back as "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS". Comparing the first
// ten characters as text is exact for both and sidesteps timezones entirely -
// no Date object ever has to be built just to ask "is this day inside that
// range".
const dayKey = (value) => (value ? String(value).slice(0, 10) : "");

const pad2 = (n) => String(n).padStart(2, "0");
const makeDayKey = (year, monthIndex, day) =>
  `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;

const daysInMonth = (year, monthIndex) =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

const LeaveWhoElseIsOff = ({ leave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const startKey = dayKey(leave?.StartDateTime);
  const endKey = dayKey(leave?.EndDateTime) || startKey;

  // The month either side of the request. Derived from the date strings rather
  // than from Date arithmetic so a request spanning a month or year boundary
  // still produces the right run of months.
  const months = useMemo(() => {
    if (!startKey) return [];
    const [startYear, startMonth] = startKey.split("-").map(Number);
    const [endYear, endMonth] = (endKey || startKey).split("-").map(Number);

    const first = startYear * 12 + (startMonth - 1) - 1; // one month before
    const last = endYear * 12 + (endMonth - 1) + 1; // one month after

    const out = [];
    for (let index = first; index <= last; index += 1) {
      out.push({ year: Math.floor(index / 12), monthIndex: index % 12 });
    }
    return out;
  }, [startKey, endKey]);

  const windowStart = months.length
    ? makeDayKey(months[0].year, months[0].monthIndex, 1)
    : "";
  const windowEnd = months.length
    ? makeDayKey(
        months[months.length - 1].year,
        months[months.length - 1].monthIndex,
        daysInMonth(
          months[months.length - 1].year,
          months[months.length - 1].monthIndex
        )
      )
    : "";

  useEffect(() => {
    if (!windowStart || !windowEnd) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await searchStaffLeaveApi([
          {
            staffleave_data: {
              RangeStartDateTime: windowStart,
              RangeEndDateTime: windowEnd,
              // Don't report the request back as a clash with itself.
              ...(leave?.Id ? { ExcludeId: leave.Id } : {}),
            },
          },
        ]);
        if (cancelled) return;
        if (response?.status === 200) {
          setRows(response.staffleave_list || []);
        } else {
          setError(response?.message || "Could not load other leave");
        }
      } catch (e) {
        if (!cancelled) setError("Could not load other leave");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [windowStart, windowEnd, leave?.Id]);

  // Everyone off, day by day. Built once rather than re-scanning every row for
  // each of the ~93 cells drawn below.
  const byDay = useMemo(() => {
    const map = new Map();
    rows
      .filter((row) => String(row?.StaffLeaveStatus_Id) !== DECLINED_STATUS)
      .forEach((row) => {
        const from = dayKey(row?.StartDateTime);
        const to = dayKey(row?.EndDateTime) || from;
        if (!from) return;
        const [fy, fm, fd] = from.split("-").map(Number);
        // Walk the range a day at a time in UTC - safe across DST, month and
        // year boundaries, and capped so one bad record cannot spin forever.
        let cursor = Date.UTC(fy, fm - 1, fd);
        for (let guard = 0; guard < 400; guard += 1) {
          const date = new Date(cursor);
          const key = makeDayKey(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate()
          );
          if (key > to) break;
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(row);
          cursor += 86400000;
        }
      });
    return map;
  }, [rows]);

  // The answer to the question actually being asked: who is away on the days
  // this request covers.
  const clashingRows = useMemo(() => {
    if (!startKey) return [];
    return rows
      .filter((row) => String(row?.StaffLeaveStatus_Id) !== DECLINED_STATUS)
      .filter((row) => {
        const from = dayKey(row?.StartDateTime);
        const to = dayKey(row?.EndDateTime) || from;
        return from && from <= endKey && to >= startKey;
      })
      .sort((a, b) =>
        String(a?.Staff_FullName || "").localeCompare(
          String(b?.Staff_FullName || "")
        )
      );
  }, [rows, startKey, endKey]);

  if (!startKey) {
    return (
      <p className="text-muted mb-0">
        Pick the dates first and this will show who else is off then.
      </p>
    );
  }

  if (loading) return <LoadingSpinner />;

  if (error) {
    return <div className="alert alert-warning mb-0">{error}</div>;
  }

  return (
    <div>
      <div className="mb-3">
        <h6 className="mb-1">During this request</h6>
        {clashingRows.length === 0 ? (
          <p className="text-success mb-0">
            <i className="ri-checkbox-circle-line align-middle me-1" />
            Nobody else is booked off on these dates.
          </p>
        ) : (
          <ul className="list-unstyled mb-0">
            {clashingRows.map((row) => (
              <li key={row?.Id} className="mb-1">
                <span className="fw-semibold">
                  {row?.Staff_FullName || "Unnamed"}
                </span>{" "}
                <span className="text-muted">
                  {row?.StaffLeaveType_Name || "Leave"} ·{" "}
                  {dayKey(row?.StartDateTime)} to{" "}
                  {dayKey(row?.EndDateTime) || dayKey(row?.StartDateTime)}
                </span>
                {String(row?.StaffLeaveStatus_Id) !== APPROVED_STATUS && (
                  <span className="badge bg-warning-subtle text-warning ms-1">
                    {row?.StaffLeaveStatus_Name || "Pending"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <h6 className="mb-1">The month either side</h6>
      <p className="text-muted small mb-2">
        Each cell counts how many other people are off that day. The outlined
        run is this request.
      </p>
      <div className="table-responsive">
        <table className="table table-sm table-bordered align-middle mb-0 whoelse-grid">
          <thead className="table-light">
            <tr>
              <th style={{ minWidth: 60 }}>Month</th>
              {Array.from({ length: DAYS_IN_ROW }, (_, i) => (
                <th key={i} className="text-center p-0 small">
                  {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {months.map(({ year, monthIndex }) => {
              const length = daysInMonth(year, monthIndex);
              return (
                <tr key={`${year}-${monthIndex}`}>
                  <th className="small text-nowrap">
                    {MONTH_LABELS[monthIndex]} {String(year).slice(2)}
                  </th>
                  {Array.from({ length: DAYS_IN_ROW }, (_, i) => {
                    const day = i + 1;
                    // Months shorter than 31 days get inert filler rather than
                    // a ragged right edge.
                    if (day > length) {
                      return (
                        <td
                          key={day}
                          className="p-0 bg-light border-0"
                          style={{ width: 20 }}
                        />
                      );
                    }
                    const key = makeDayKey(year, monthIndex, day);
                    const off = byDay.get(key) || [];
                    const isThisRequest = key >= startKey && key <= endKey;
                    const names = off
                      .map(
                        (row) =>
                          `${row?.Staff_FullName || "Unnamed"} (${
                            row?.StaffLeaveType_Name || "Leave"
                          })`
                      )
                      .join("\n");
                    return (
                      <td
                        key={day}
                        className={`p-0 text-center small ${
                          off.length ? "bg-warning-subtle fw-semibold" : ""
                        }`}
                        style={{
                          width: 20,
                          outline: isThisRequest
                            ? "2px solid var(--bs-primary)"
                            : undefined,
                          outlineOffset: isThisRequest ? "-2px" : undefined,
                        }}
                        title={
                          off.length
                            ? `${key}\n${names}`
                            : `${key}\nNobody else off`
                        }
                      >
                        {off.length || ""}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaveWhoElseIsOff;
